// src/components/article/EditorArticleList.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IoDocumentTextOutline,
  IoCalendarOutline,
  IoCheckmarkCircleOutline,
  IoCloseCircleOutline,
  IoTimerOutline,
  IoChatbubbleOutline
} from 'react-icons/io5';
import Spinner from '../common/Spinner';
import { formatDate } from '../../utils/formatters';
import articleService from '../../services/articleService';
import ArticleFilters from './ArticleFilters';

const EditorArticleList = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    dateRange: { from: '', to: '' },
    accessType: '' // '', 'free', 'paid'
  });
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  // Status badge colors
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'under review':
        return 'info';
      case 'submitted':
        return 'secondary';
      case 'rejected':
        return 'danger';
      default:
        return 'info';
    }
  };

  // Access badge class
  const getAccessClass = (accessType) => {
    const v = (accessType || '').toLowerCase();
    return v === 'paid' ? 'paid' : v === 'free' ? 'free' : '';
  };

  useEffect(() => {
    fetchEditorArticles();
  }, []);

  useEffect(() => {
    if (articles.length > 0) {
      applyFiltersAndSearch();
    } else {
      setFilteredArticles([]);
    }
  }, [searchTerm, filters, articles]);

  const fetchEditorArticles = async (page = 1, limit = 10) => {
    setLoading(true);
    setError(null);
    try {
      const result = await articleService.getEditorArticles(page, limit);
      if (result.success) {
        setArticles(result.data.articles);
        setFilteredArticles(result.data.articles);
        setPagination(result.data.pagination);
      } else {
        setError('Failed to fetch articles');
      }
    } catch (err) {
      console.error('Error fetching editor articles:', err);
      setError('An error occurred while fetching articles. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  // Build a compact string from structured abstract fields for preview & search fallback
  const buildStructuredAbstractPreview = (a) => {
    const parts = [];
    if (a?.background) parts.push(`Background: ${a.background}`);
    if (a?.methods) parts.push(`Methods: ${a.methods}`);
    if (a?.results) parts.push(`Results: ${a.results}`);
    if (a?.conclusions) parts.push(`Conclusions: ${a.conclusions}`);
    if (a?.introduction) parts.push(`Introduction: ${a.introduction}`);
    return parts.join(' • ');
  };

  const textIncludes = (text, term) => (text || '').toLowerCase().includes(term);

  const applyFiltersAndSearch = () => {
    let results = [...articles];

    // Apply search (includes structured abstract fields + accessType)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      results = results.filter((article) => {
        const haystacks = [
          article.title,
          article.abstract,
          article.background,
          article.methods,
          article.results,
          article.conclusions,
          article.introduction,
          article.accessType
        ];
        return haystacks.some((h) => textIncludes(h, term));
      });
    }

    // Apply status filter
    if (filters.status) {
      results = results.filter(
        (article) => article.status?.toLowerCase() === filters.status.toLowerCase()
      );
    }

    // Apply accessType filter if present
    if (filters.accessType) {
      const want = filters.accessType.toLowerCase();
      results = results.filter((article) => (article.accessType || '').toLowerCase() === want);
    }

    // Apply date range filters
    if (filters.dateRange.from || filters.dateRange.to) {
      results = results.filter((article) => {
        const createdDate = new Date(article.createdAt);

        if (filters.dateRange.from && filters.dateRange.to) {
          const fromDate = new Date(filters.dateRange.from);
          const toDate = new Date(filters.dateRange.to);
          toDate.setHours(23, 59, 59, 999);
          return createdDate >= fromDate && createdDate <= toDate;
        }

        if (filters.dateRange.from) {
          const fromDate = new Date(filters.dateRange.from);
          return createdDate >= fromDate;
        }

        if (filters.dateRange.to) {
          const toDate = new Date(filters.dateRange.to);
          toDate.setHours(23, 59, 59, 999);
          return createdDate <= toDate;
        }

        return true;
      });
    }

    setFilteredArticles(results);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    fetchEditorArticles(newPage, pagination.limit);
  };

  const handleViewArticle = (articleId) => {
    navigate(`/articles/${articleId}`);
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return <IoCheckmarkCircleOutline className="text-emerald-600 dark:text-emerald-300" />;
      case 'pending':
        return <IoTimerOutline className="text-amber-600 dark:text-amber-300" />;
      case 'under review':
        return <IoTimerOutline className="text-blue-600 dark:text-blue-300" />;
      case 'submitted':
        return <IoTimerOutline className="text-slate-600 dark:text-slate-300" />;
      case 'rejected':
        return <IoCloseCircleOutline className="text-red-600 dark:text-red-300" />;
      default:
        return <IoTimerOutline className="text-amber-600 dark:text-amber-300" />;
    }
  };

  // Truncate helper (safe for undefined)
  const truncate = (str, len = 150) => {
    const s = (str || '').trim();
    if (!s) return '';
    return s.length > len ? `${s.substring(0, len)}...` : s;
  };

  const StatusPill = ({ status }) => {
    const key = getStatusColor(status);
    const map = {
      success:
        'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-200 dark:ring-emerald-900/40',
      warning:
        'bg-amber-50 text-amber-700 ring-1 ring-amber-100 dark:bg-amber-950/40 dark:text-amber-200 dark:ring-amber-900/40',
      info:
        'bg-blue-50 text-blue-700 ring-1 ring-blue-100 dark:bg-blue-950/40 dark:text-blue-200 dark:ring-blue-900/40',
      secondary:
        'bg-slate-50 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800/50 dark:text-slate-200 dark:ring-slate-700/60',
      danger:
        'bg-red-50 text-red-700 ring-1 ring-red-100 dark:bg-red-950/40 dark:text-red-200 dark:ring-red-900/40'
    };
    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${map[key] || map.info}`}>
        {status}
      </span>
    );
  };

  const AccessPill = ({ accessType }) => {
    const cls = getAccessClass(accessType);
    if (!cls) {
      return (
        <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold bg-slate-50 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800/50 dark:text-slate-200 dark:ring-slate-700/60">
          —
        </span>
      );
    }

    const isPaid = cls === 'paid';
    return (
      <span
        className={[
          'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1',
          isPaid
            ? 'bg-violet-50 text-violet-700 ring-violet-100 dark:bg-violet-950/40 dark:text-violet-200 dark:ring-violet-900/40'
            : 'bg-cyan-50 text-cyan-700 ring-cyan-100 dark:bg-cyan-950/40 dark:text-cyan-200 dark:ring-cyan-900/40'
        ].join(' ')}
      >
        {isPaid ? 'Paid' : 'Free'}
      </span>
    );
  };

  return (
    <div className="editor-article-list w-full pb-24">
      <div className="w-full">
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                Articles for Review
              </h2>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                Filter, review and open any submitted article assigned to you.
              </p>
            </div>

            <div className="w-full sm:w-auto">
              <ArticleFilters
                onSearchChange={handleSearchChange}
                onFilterChange={handleFilterChange}
                status={filters.status}
                dateRange={filters.dateRange}
                accessType={filters.accessType}
              />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-14">
              <Spinner size="medium" />
            </div>
          ) : error ? (
            <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-5 text-red-700 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-200">
              <p className="text-sm font-medium">{error}</p>
              <button
                className="mt-4 inline-flex items-center justify-center rounded-xl bg-blue-600 px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                onClick={() => fetchEditorArticles()}
                type="button"
              >
                Try Again
              </button>
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="mt-8 rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-10 text-center dark:border-slate-800 dark:bg-slate-950/40">
              <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 ring-1 ring-blue-100 dark:bg-blue-950/40 dark:text-blue-200 dark:ring-blue-900/40">
                <IoDocumentTextOutline className="text-2xl" />
              </div>
              <h3 className="mt-4 text-base font-bold text-slate-900 dark:text-white">
                No Articles Found
              </h3>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                {searchTerm.trim() ||
                filters.status ||
                filters.dateRange.from ||
                filters.dateRange.to ||
                filters.accessType
                  ? 'No articles match your search criteria.'
                  : 'There are no articles assigned to you for review.'}
              </p>
            </div>
          ) : (
            <>
              <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[980px] border-collapse">
                    <thead className="bg-slate-50 dark:bg-slate-950/40">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                          Title
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                          Submission Date
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                          Status
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                          Access
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                          Comments
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                          Actions
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                      {filteredArticles.map((article) => {
                        const preview =
                          article.abstract && article.abstract.trim().length > 0
                            ? article.abstract
                            : buildStructuredAbstractPreview(article);

                        const access = (article.accessType || '').toLowerCase();

                        return (
                          <tr
                            key={article._id}
                            className="group transition hover:bg-slate-50 dark:hover:bg-slate-800/40"
                          >
                            <td className="px-5 py-4 align-top">
                              <div className="text-sm font-semibold text-slate-900 dark:text-white">
                                {article.title}
                              </div>
                              {preview && (
                                <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                                  {truncate(preview, 150)}
                                </div>
                              )}
                            </td>

                            <td className="px-5 py-4 align-top">
                              <div className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                                <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                                  <IoCalendarOutline />
                                </span>
                                <span className="font-medium">{formatDate(article.createdAt)}</span>
                              </div>
                            </td>

                            <td className="px-5 py-4 align-top">
                              <div className="flex items-center gap-2">
                                <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800">
                                  {getStatusIcon(article.status)}
                                </span>
                                <StatusPill status={article.status} />
                              </div>
                            </td>

                            <td className="px-5 py-4 align-top">
                              <AccessPill accessType={access} />
                            </td>

                            <td className="px-5 py-4 align-top">
                              {article.comment ? (
                                <div className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
                                  <IoChatbubbleOutline className="mt-0.5 shrink-0 text-slate-500 dark:text-slate-400" />
                                  <span className="line-clamp-3">{article.comment}</span>
                                </div>
                              ) : (
                                <span className="text-sm text-slate-500 dark:text-slate-400">
                                  No comments
                                </span>
                              )}
                            </td>

                            <td className="px-5 py-4 align-top">
                              <button
                                className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800/60 dark:hover:text-white"
                                title="View Article"
                                onClick={() => handleViewArticle(article._id)}
                                type="button"
                                aria-label="View article"
                              >
                                <IoDocumentTextOutline />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {pagination.totalPages > 1 && (
                <div className="mt-6 flex flex-col items-center justify-between gap-3 sm:flex-row">
                  <button
                    className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800/60"
                    disabled={pagination.page === 1}
                    onClick={() => handlePageChange(pagination.page - 1)}
                    type="button"
                  >
                    Previous
                  </button>

                  <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>

                  <button
                    className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800/60"
                    disabled={pagination.page === pagination.totalPages}
                    onClick={() => handlePageChange(pagination.page + 1)}
                    type="button"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditorArticleList;
